import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { UnifiedSearchComponent } from '../../features/search/unified/unified-search.component';
import { SearchGateway } from '../../features/search/unified/search-gateway';
import { RealSearchGateway } from '../../features/search/unified/real-search-gateway';

/**
 * Global search overlay (EB-3621 Plan 5.3) — hosts the unified search + quicklinks in a
 * MatDialog so it is reachable from any page via the header search bar or Ctrl/Cmd+K.
 *
 * Closes on the search's `activated` output, i.e. as soon as the user picks something —
 * NOT on navigation alone: jumping to the page the operator is already on is a same-URL
 * navigation that Angular ignores, so no NavigationEnd fires and the overlay would hang
 * open with the chip looking unclickable (EB-4175). The NavigationEnd subscription stays
 * for route changes triggered from outside the search (e.g. browser back).
 *
 * Provides the `RealSearchGateway` (EB-4074 — server-side customer merge).
 */
@Component({
  selector: 'cmt-global-search-overlay',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UnifiedSearchComponent],
  providers: [{ provide: SearchGateway, useClass: RealSearchGateway }],
  // max-h + overflow so long result lists scroll INSIDE the overlay instead of running off the
  // bottom of the viewport (the dialog is fixed-position, so the page can't scroll to reach them).
  // No vertical padding here: the surface's `padding-block` (styles.scss) supplies the top/bottom
  // gap AND keeps the scrollbar ends clear of the rounded corners. Horizontal padding stays.
  template: `
    <div class="max-h-[86vh] overflow-y-auto overscroll-contain px-5">
      <cmt-unified-search [overlay]="true" (activated)="dialogRef.close()" />
    </div>
  `,
})
export class GlobalSearchOverlayComponent {
  protected readonly dialogRef = inject(MatDialogRef<GlobalSearchOverlayComponent>);

  constructor() {
    inject(Router).events.pipe(
      filter((event) => event instanceof NavigationEnd),
      takeUntilDestroyed(),
    ).subscribe(() => this.dialogRef.close());
  }
}
